import { useState } from 'react';
import axios, { isAxiosError } from 'axios';
import axiosInstance from '../../../ns_libs/nsAxios';
import API_ENDPOINTS from '../../../services/urls';

interface BulkUploadResponse {
    created: number;
    errors: Array<{
        line: number;
        dealName: string;
        message: string;
    }>;
}

interface UseBulkUploadDealsOptions {
    onSuccess?: (response: BulkUploadResponse) => void;
    onError?: (error: unknown) => void;
}

const useBulkUploadDeals = (organizationId?: string, options?: UseBulkUploadDealsOptions) => {
    const [isUploading, setIsUploading] = useState(false);

    const handleBulkUpload = async (file: File): Promise<BulkUploadResponse | undefined> => {
        if (!organizationId) {
            throw new Error('Organization ID is required');
        }

        setIsUploading(true);
        try {
            const presignedUrlResponse = await axiosInstance.post(
                API_ENDPOINTS.DEALS_BULK_UPLOAD.PRESIGNED_URL(organizationId),
                { file_name: file.name },
            );

            const formData = new FormData();
            Object.entries(presignedUrlResponse.data.url.fields).forEach(([key, value]) => {
                formData.append(key, value as string);
            });
            formData.append('file', file);

            await axios.post(presignedUrlResponse.data.url.url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            const s3Key = presignedUrlResponse.data.url.fields.key;
            const response = await axiosInstance.post<BulkUploadResponse>(
                API_ENDPOINTS.DEALS_BULK_UPLOAD.PROCESS(organizationId),
                { key: s3Key },
            );

            if (options?.onSuccess) {
                options.onSuccess(response.data);
            }

            return response.data;
        } catch (error: unknown) {
            if (isAxiosError(error)) {
                const errorMessage = error.response?.data?.detail || error.message;
                if (options?.onError) {
                    options.onError(new Error(errorMessage));
                    return undefined;
                }
                throw new Error(errorMessage);
            }
            if (options?.onError) {
                options.onError(error);
                return undefined;
            }
            throw error;
        } finally {
            setIsUploading(false);
        }
    };

    return {
        handleBulkUpload,
        isUploading,
    };
};

export default useBulkUploadDeals;
